import React, { useCallback, useEffect, useState } from "react";
import { savingsAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { sanitizeAmount } from "../pos/utils/amountEntry";
import { formatDate, formatDateTime, todayIso, monthStartIso } from "../utils/dates";
import EmptyState from "../components/EmptyState";

/**
 * The owner's own money: the cash taken home or moved out of the drawer, and
 * what has been paid out of it since.
 *
 * Never revenue and never an expense — a figure that only moves when the
 * owner moves money, so it can be trusted to match what is really at home.
 * An expense or a supplier paid from it is recorded on its own screen and
 * lowers this balance there.
 */
const PERIOD_LINES = [
  "taken_home",
  "added_by_hand",
  "returned_to_drawer",
  "paid_expenses",
  "paid_suppliers",
  "personal_use",
  "other_out",
  "owner_money_in",
];

const WITHDRAW_REASONS = ["personal", "other"];

const field =
  "mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";

const Savings = () => {
  const { t } = useI18n();
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);

  const [data, setData] = useState(null);
  const [range, setRange] = useState({ start: monthStartIso(), end: todayIso() });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(null); // { kind: "deposit" | "withdraw", amount, reason, note }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: overview } = await savingsAPI.overview({
        start_date: range.start,
        end_date: range.end,
        limit: 100,
      });
      setData(overview);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || t("savings.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (kind) => {
    setNotice("");
    setForm({ kind, amount: "", reason: kind === "withdraw" ? "personal" : "deposit", note: "" });
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    const payload = {
      amount: parseFloat(form.amount),
      note: form.note || undefined,
    };

    try {
      if (form.kind === "deposit") {
        await savingsAPI.deposit(payload);
      } else {
        await savingsAPI.withdraw({ ...payload, reason: form.reason });
      }
      setForm(null);
      setNotice(t("savings.saved"));
      await load();
    } catch (err) {
      setError(
        err.response?.data?.code === "SAFE_INSUFFICIENT"
          ? t("savings.insufficient")
          : err.response?.data?.message || t("savings.saveFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">{t("savings.title")}</h1>
        <p className="text-gray-600">{t("savings.subtitle")}</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        {loading ? (
          <p className="text-gray-500 py-8 text-center">{t("common.loading")}</p>
        ) : (
          data && (
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  {t("savings.balance")}
                </p>
                <p className="text-4xl font-extrabold tabular-nums text-emerald-700" dir="ltr">
                  {money(data.balance)}
                </p>
                <p className="mt-1 text-sm text-gray-500">{t("savings.balanceHint")}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => open("deposit")}
                  className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
                >
                  ＋ {t("savings.add")}
                </button>
                <button
                  type="button"
                  onClick={() => open("withdraw")}
                  className="px-5 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 font-semibold text-gray-800"
                >
                  − {t("savings.take")}
                </button>
              </div>
            </div>
          )
        )}

        {form && (
          <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-3 border-t border-gray-100 pt-6">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("savings.amount", { currency })}
              </span>
              <input
                type="text"
                inputMode="decimal"
                dir="ltr"
                required
                autoFocus
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: sanitizeAmount(e.target.value) })}
                className={field}
              />
            </label>

            {form.kind === "withdraw" && (
              <label className="block">
                <span className="text-sm font-medium text-gray-700">{t("savings.reasonTitle")}</span>
                <select
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className={`${field} bg-white`}
                >
                  {WITHDRAW_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {t(`savings.withdrawReasons.${reason}`)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="block">
              <span className="text-sm font-medium text-gray-700">{t("savings.note")}</span>
              <input
                type="text"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className={field}
              />
            </label>

            <div className="sm:col-span-3 flex gap-3">
              <button
                type="submit"
                disabled={busy || !(parseFloat(form.amount) > 0)}
                className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
              >
                {busy ? t("common.saving") : t("savings.save")}
              </button>
              <button
                type="button"
                onClick={() => setForm(null)}
                className="px-4 py-2.5 rounded-lg text-gray-600 hover:bg-gray-50"
              >
                {t("common.cancel")}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-3">
            <label className="block">
              <span className="text-xs text-gray-500">{t("common.from")}</span>
              <input
                type="date"
                value={range.start}
                onChange={(e) => setRange({ ...range, start: e.target.value })}
                className="block mt-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-gray-500">{t("common.to")}</span>
              <input
                type="date"
                value={range.end}
                onChange={(e) => setRange({ ...range, end: e.target.value })}
                className="block mt-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              />
            </label>
          </div>
          <p className="text-sm text-gray-500">
            {t("savings.forPeriod", { from: formatDate(range.start), to: formatDate(range.end) })}
          </p>
        </div>

        {data && (
          <>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {PERIOD_LINES.filter((line) => data.period[line] > 0 || line === "taken_home").map(
                (line) => (
                  <div key={line} className="rounded-lg bg-gray-50 p-4">
                    <dt className="text-sm text-gray-500">{t(`savings.period.${line}`)}</dt>
                    <dd className="mt-1 text-xl font-bold tabular-nums text-gray-900" dir="ltr">
                      {money(data.period[line])}
                    </dd>
                  </div>
                ),
              )}
            </dl>
            <p className="mt-4 text-xs text-gray-500">{t("savings.personalNote")}</p>

            <h2 className="mt-8 mb-2 text-lg font-semibold text-gray-800">{t("savings.history")}</h2>
            {data.movements.length === 0 ? (
              <EmptyState screen="savings" message={t("savings.none")} />
            ) : (
              <ul className="divide-y divide-gray-100">
                {data.movements.map((movement) => (
                  <li key={movement.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">
                        {t(`savings.reasons.${movement.reason}`)}
                        {movement.note && (
                          <span className="font-normal text-gray-600"> · {movement.note}</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        {movement.created_by_name || "—"} · {formatDateTime(movement.created_at)}
                      </p>
                    </div>
                    <span
                      className={`font-semibold tabular-nums whitespace-nowrap ${
                        movement.direction === "in" ? "text-emerald-700" : "text-gray-900"
                      }`}
                      dir="ltr"
                    >
                      {movement.direction === "in" ? "+" : "−"}
                      {money(movement.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Savings;
